
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import LineChartFilterable from '../../filterables/LineChartFilterable.vue';
import FilterWidget from '../shared/FilterWidget.vue';
import {
	PermissionsGroup,
} from '@/store/models.def';
import gdbx from '@/store/modules/gdbx';
import * as echarts from 'echarts';
import moment, { Moment } from 'moment';
import { GobiColor } from '@/helpers/color';
import { TooltipFormatter } from '../../elements/charts/helpers/tooltipFormatter';
import { getTableName } from '@/worker/db/sqlHelpers';

@Component({
	components: {
		LineChartFilterable,
	},
})
export default class ForecastSalesProfitComparisonLineChart extends FilterWidget {
	@Prop({ default: () => [] }) public forecastSales!: number[];
	@Prop({ default: () => [] }) public forecastNetProfits!: number[];

	public get permissionIds(): PermissionsGroup[] {
		return ['transactions'];
	}

	public sales: number[] = [];
	public grossProfits: number[] = [];
	public expenses: number[] = [];

	public get dateFormatted() {
		return this.selectedDateRange;
	}
	public get netProfits() {
		return this.grossProfits.map(
			(grossProfits, i) => grossProfits - this.expenses[i],
		);
	}
	public get monthValues() {
		const [d0, d1] = this.selectedDateRange.map((d) => moment(d));
		const period = d1.diff(d0, 'month') + 1;
		const result: Moment[] = [];
		for (let i = 0; i < period; i++) {
			result.push(d0.clone());
			d0.add(1, 'month');
		}
		return result;
	}
	public get months() {
		return this.monthValues.map((m) => m.format('MMM YYYY'));
	}

	public filterIds: Array<
		'dateAsOf' | 'dateRange' | 'stockItems' | 'agents' | 'customers' | 'suppliers'
	> = ['dateRange'];

	public get currency() {
		return gdbx.currencySymbol;
	}

	public get formatter() {
		return TooltipFormatter.lineChart;
	}

	public get expensiveHook() {
		const { selectedDateRange } = this;
		return JSON.stringify([selectedDateRange]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.4);

		const queryHead = await this._ensureTablesLoaded(
			['transactions', 'stockBalances'] as any[],
		);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.4, 0.6);

		const txnTable = getTableName('transactions');
		const sbTable = getTableName('stockBalances');
		const months = this.monthValues;
		const monthCount = months.length;

		const monthCases = months.map((m, i) => {
			const s = m.clone().startOf('month').valueOf();
			const e = m.clone().endOf('month').valueOf();
			return `WHEN TRY_CAST("DOCDATE" AS BIGINT)`
				+ ` BETWEEN ${s} AND ${e} THEN ${i}`;
		}).join(' ');

		const [txnRows, sbRows] = await Promise.all([
			queryHead.rawQuery(`
				SELECT "ACCTYPE" as accType,
					CASE ${monthCases} END as mIdx,
					SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as totalAmt
				FROM ${txnTable}
				WHERE TRY_CAST("DOCDATE" AS BIGINT)
					BETWEEN ${this.selectedDateRange[0]}
					AND ${this.selectedDateRange[1]}
					AND "ACCTYPE" IN ('SA', 'SL', 'CO', 'EP')
				GROUP BY "ACCTYPE", mIdx
			`),
			queryHead.rawQuery(`
				SELECT "SYEAR" as yr, "SMONTH" as mo,
					SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as totalAmt
				FROM ${sbTable}
				GROUP BY "SYEAR", "SMONTH"
			`),
		]);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.6, 0.9);

		const accSums: { [accType: string]: number[] } = {
			SA: new Array(monthCount).fill(0),
			SL: new Array(monthCount).fill(0),
			CO: new Array(monthCount).fill(0),
			EP: new Array(monthCount).fill(0),
		};
		for (const r of txnRows) {
			const idx = Number(r.mIdx);
			const at = String(r.accType);
			if (idx >= 0 && idx < monthCount && accSums[at]) {
				accSums[at][idx] = Number(r.totalAmt) || 0;
			}
		}

		const sbMap: { [key: string]: number } = {};
		for (const r of sbRows) {
			const key = Number(r.yr) + '-' + Number(r.mo);
			sbMap[key] = (sbMap[key] || 0) + (Number(r.totalAmt) || 0);
		}

		const salesArr: number[] = [];
		const grossProfitsArr: number[] = [];
		const expensesArr: number[] = [];

		for (let i = 0; i < monthCount; i++) {
			const m = months[i];
			const salesAmt = -(accSums.SA[i] + accSums.SL[i]);
			salesArr.push(salesAmt);

			const costAmt = -accSums.CO[i];
			const prev = m.clone().subtract(1, 'month');
			const prevKey = prev.year() + '-' + (prev.month() + 1);
			const currKey = m.year() + '-' + (m.month() + 1);
			const stockMov = (sbMap[prevKey] || 0) - (sbMap[currKey] || 0);
			grossProfitsArr.push(salesAmt + costAmt + stockMov);

			expensesArr.push(accSums.EP[i]);
		}

		this.sales = salesArr;
		this.grossProfits = grossProfitsArr;
		this.expenses = expensesArr;

		this.saveHistory('sales', 'grossProfits', 'expenses');
		this.generateLoadingText(1);
	}

	public get xAxisData() {
		return this.months;
	}
	public get series(): echarts.ECharts[] | any {
		const result = [
			{
				name: 'Predicted Sales',
				type: 'line',
				smooth: true,
				barWidth: '40%',
				color: GobiColor.COLOR1,
				lineStyle: {
					type: 'dashed',
					opacity: 0.5,
				},
				data: this.forecastSales,
			},
			{
				name: 'Actual Sales',
				type: 'line',
				smooth: true,
				barWidth: '40%',
				color: GobiColor.COLOR1,
				data: this.sales,
			},
			{
				name: 'Predicted Net Profit',
				type: 'line',
				smooth: true,
				barWidth: '40%',
				color: GobiColor.COLOR5,
				lineStyle: {
					type: 'dashed',
					opacity: 0.8,
				},
				data: this.forecastNetProfits,
			},
			{
				name: 'Actual Net Profit',
				type: 'line',
				smooth: true,
				barWidth: '40%',
				color: GobiColor.COLOR5,
				data: this.netProfits,
			},
		];
		return result;
	}

	public onHovering(value: boolean, filterId: string) {
		this.$emit('hovering', value, filterId);
	}
	public onEditing(value: boolean, filterId: string) {
		this.$emit('editing', value, filterId);
	}
}
